import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Building2, Tags, Truck } from 'lucide-react'
import apiClient from '@/api/client'
import { Stat } from '@/components/hr/shared'
import { useCurrency } from '@/contexts/CurrencyContext'
import { getStoredUserRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'
import type { TradeAccount } from '@/types/trade'
import { DeliveriesTab } from './DeliveriesTab'
import { ClientsTab } from './ClientsTab'
import { PricesTab } from './PricesTab'

// Trade (F-TRD-N1): cafés the tenant supplies on account. Deliveries are what
// staff post in each client's WhatsApp group; Clients and Prices are set up
// once by the owner or a manager. Invoicing comes in a later phase.

export type TradeTab = 'deliveries' | 'clients' | 'prices'

export interface TradeSearch {
  tab?: TradeTab
  client?: string
  delivery?: string
}

export function tradeRole() {
  const role = getStoredUserRole()
  return {
    role,
    canEnter: role === 'admin' || role === 'manager' || role === 'counter',
    canSetup: role === 'admin' || role === 'manager',
  }
}

export function useTradeAccounts(status: 'open' | 'closed' | 'all' = 'open') {
  const q = useQuery({ queryKey: ['trade', 'accounts', status], queryFn: () => apiClient.tradeListAccounts(status) })
  return { accounts: (q.data?.data as TradeAccount[] | undefined) ?? [], isLoading: q.isLoading }
}

const TABS: { id: TradeTab; label: string; icon: React.ReactNode; hideFromCounter?: boolean }[] = [
  { id: 'deliveries', label: 'Deliveries', icon: <Truck className="h-4 w-4" /> },
  { id: 'clients', label: 'Clients', icon: <Building2 className="h-4 w-4" /> },
  { id: 'prices', label: 'Trade prices', icon: <Tags className="h-4 w-4" />, hideFromCounter: true },
]

export function TradePage({ search }: { search: TradeSearch }) {
  const navigate = useNavigate()
  const { formatCurrency } = useCurrency()
  const { role } = tradeRole()
  const tabs = TABS.filter((t) => !t.hideFromCounter || role !== 'counter')
  const tab = tabs.some((t) => t.id === search.tab) ? (search.tab as TradeTab) : 'deliveries'
  const go = (next: Partial<TradeSearch>, replace = false) => navigate({ to: '/admin/trade', search: { ...search, ...next }, replace })
  const { accounts } = useTradeAccounts()

  const open = accounts.reduce((s, a) => s + a.open_value, 0)
  const drafts = accounts.reduce((s, a) => s + a.draft_count, 0)

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Trade</h1>
          <p className="mt-1 text-muted-foreground">Deliveries to the cafés you supply, billed every cycle. No tax is charged on trade sales.</p>
        </div>
        {accounts.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Clients" value={accounts.filter((a) => a.status === 'active').length} />
            <Stat label="Delivered, not yet billed" value={formatCurrency(open)} />
            <Stat label="Drafts to confirm" value={drafts} tone={drafts > 0 ? 'warn' : undefined} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => go({ tab: t.id, client: undefined, delivery: undefined })}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'deliveries' && (
        <DeliveriesTab deliveryId={search.delivery} onDelivery={(delivery) => go({ delivery })} onSetUp={() => go({ tab: 'clients' })} />
      )}
      {tab === 'clients' && <ClientsTab clientId={search.client} onClient={(client) => go({ client })} />}
      {tab === 'prices' && <PricesTab />}
    </div>
  )
}
